/**
 * Wire types — Brief 5 B6 Guardianship / Curadoria (Sprint Frontend Brief 5 · Tela 2).
 *
 * Hand-written (decisão do projeto) espelhando o serializer REAL do Engine
 * (apps/engine/src/modules/adversarial/adversarial.serialization.ts
 *  → SerializedGuardianship + dto/guardianship.dto.ts). Triangulado campo-a-campo
 * (R15) — NÃO derivado do mockup (guardianship.html é ADR-114, obsoleto).
 *
 * Gate: `legal-capacity-management` (tierMin Regulated — catalog.ts).
 *
 * 🔴 DRIFT ADR-114 → ADR-047 (handoff §2): o mockup usa termos que o backend
 * rejeita (@IsEnum). A fonte da verdade são os enums canônicos de `@lume/shared`
 * (importados aqui, NÃO hand-written). O card/detalhe renderiza os campos REAIS
 * do serializer — `ward`/`guardian` (nomes), CPF mascarado, `relationship`,
 * `typeLabel` e `legalBasisRef` do mockup NÃO existem na API e NÃO são fabricados.
 *
 * Honest-defers visíveis (balde B): auto-cálculo de effectiveUntil, upload real
 * de documento (coletamos `documentReferenceId` como string), join de pessoa
 * (exibimos `wardId`/`guardianId` opacos — person-picker é melhoria futura),
 * edição (PATCH) e aprovação/co-signer Regulated.
 */

import { GUARDIANSHIP_BASES, GUARDIANSHIP_STATUSES, GUARDIANSHIP_TYPES } from '@lume/shared';

import type {
  GuardianshipBasis,
  GuardianshipScope,
  GuardianshipStatus,
  GuardianshipType,
} from '@lume/shared';

export { GUARDIANSHIP_BASES, GUARDIANSHIP_STATUSES, GUARDIANSHIP_TYPES };
export type { GuardianshipBasis, GuardianshipScope, GuardianshipStatus, GuardianshipType };

// ─── Serializer real (GET /v1/guardianships[/:id], POST, DELETE) ──────────────

export interface GuardianshipWireItem {
  id: string;
  tenantId: string;
  wardId: string;
  guardianId: string;
  type: GuardianshipType;
  scope: GuardianshipScope;
  basis: GuardianshipBasis | null;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  documentReferenceId: string | null;
  status: GuardianshipStatus;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  version: number;
}

// ─── Requests (DTO real) ──────────────────────────────────────────────────────

export interface CreateGuardianshipRequest {
  wardId: string;
  guardianId: string;
  type: GuardianshipType;
  scope?: GuardianshipScope;
  basis?: GuardianshipBasis;
  effectiveFrom?: string;
  effectiveUntil?: string;
  documentReferenceId?: string;
}

export interface RevokeGuardianshipRequest {
  reason: string;
}

// ─── Mapas UI (enum REAL → label pt-BR) ───────────────────────────────────────
//
// Apresentamos os 5 valores canônicos do enum (ADR-047), NÃO os 5 termos do
// mockup. O mapeamento mental ADR-114→ADR-047 está no header/handoff §2.

export const GUARDIANSHIP_TYPE_LABEL: Record<GuardianshipType, { label: string; hint: string }> = {
  'legal-guardian': {
    label: 'Tutela / guarda legal',
    hint: 'Tutela de menor, pátrio poder ou guarda determinada por juízo (CC art. 1.728).',
  },
  curator: {
    label: 'Curatela',
    hint: 'Representação de maior incapaz, total ou parcial (CC art. 1.767).',
  },
  'authorized-caregiver': {
    label: 'Cuidador autorizado',
    hint: 'Cuidador designado para atos específicos, sem incapacidade declarada.',
  },
  temporary: {
    label: 'Temporária',
    hint: 'Representação por prazo definido — procuração ou acordo familiar (renovável).',
  },
  'emergency-contact': {
    label: 'Contato de emergência',
    hint: 'Representação restrita a situações de urgência.',
  },
};

export const GUARDIANSHIP_BASIS_LABEL: Record<GuardianshipBasis, string> = {
  'court-order': 'Ordem judicial',
  parental: 'Pátrio poder / parental',
  'attorney-power': 'Procuração',
  'declared-by-titular': 'Declarada pelo titular',
  'emergency-protocol': 'Protocolo de emergência',
};

export const GUARDIANSHIP_STATUS_LABEL: Record<GuardianshipStatus, string> = {
  active: 'Ativa',
  suspended: 'Suspensa',
  revoked: 'Revogada',
  expired: 'Expirada',
};

/** Campos do `GuardianshipScope` canônico (@lume/shared) — checklist estruturado. */
export const GUARDIANSHIP_SCOPE_FIELDS: readonly {
  key: keyof Omit<GuardianshipScope, 'customFields'>;
  label: string;
}[] = [
  { key: 'medicalDecisions', label: 'Decisões médicas' },
  { key: 'educationalDecisions', label: 'Decisões educacionais' },
  { key: 'legalDocuments', label: 'Assinar documentos legais' },
  { key: 'financial', label: 'Atos financeiros' },
  { key: 'travel', label: 'Autorizar viagem' },
  { key: 'pickup', label: 'Buscar / retirar' },
];

// ─── Helpers derivados (campos reais, sem fabricar) ──────────────────────────

/** Resumo legível do scope: labels ativos, ou null quando vazio. */
export function summarizeScope(scope: GuardianshipScope): string | null {
  const active = GUARDIANSHIP_SCOPE_FIELDS.filter(({ key }) => scope[key] === true).map(
    ({ label }) => label,
  );
  return active.length > 0 ? active.join(' · ') : null;
}

const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 183;

/**
 * "Expira em breve": status ativa + `effectiveUntil` dentro de ~6 meses a partir
 * de `now`. `now` é injetável para determinismo nos testes.
 */
export function isExpiringSoon(
  item: Pick<GuardianshipWireItem, 'status' | 'effectiveUntil'>,
  now: Date = new Date(),
): boolean {
  if (item.status !== 'active' || item.effectiveUntil === null) return false;
  const until = new Date(item.effectiveUntil).getTime();
  if (Number.isNaN(until)) return false;
  const delta = until - now.getTime();
  return delta >= 0 && delta <= SIX_MONTHS_MS;
}
